import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Page } from 'react-pdf'
import { useDroppable, useDndContext } from '@dnd-kit/core'
import { PdfElement } from 'apps/studio/components/pdfViewer/elements/PdfElement'
import { PdfTable } from 'apps/studio/components/pdfViewer/elements/PdfTable'
import {
  pagesSelector,
  layoutsSelector,
  activePageSelector,
  pageDefaultPositionSelector,
  visibleStyledElementsWorkspaceSelector,
} from 'apps/studio/store/selectors/pdf'
import {
  sectionsSelector,
  sheetsSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { pdfPageMetaByPageNumberSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import { currentZoomSelector } from 'apps/studio/store/selectors/studioSettings'
import { isTrueStringMatch } from 'apps/builder/lib/utils'
import { appSettingsActions } from 'apps/studio/store/reducers/settings/appSettings'
const { selectSheet } = appSettingsActions

export const PdfPage = ({ width, pageNumber, onLoad }) => {
  const dispatch = useDispatch()
  const [guidelineX, setGuidelineX] = useState(null)
  const [guidelineY, setGuidelineY] = useState(null)
  const pages = useSelector(pagesSelector)
  const layouts = useSelector(layoutsSelector)
  const sections = useSelector(sectionsSelector)
  const sheets = useSelector(sheetsSelector)
  const currentZoom = useSelector(currentZoomSelector)
  const thisPage = pages.find(page => page.page_number === pageNumber)
  const pageLayout = layouts.find(
    ({ page_id, parent_layout_id }) => page_id === thisPage.id && !parent_layout_id
  )
  const tableLayouts = layouts.filter(
    l => l.page_id === thisPage.id && l.layout_type === 'table'
  )
  const pageSection = sections.find(
    section => section.id === pageLayout.section_id
  )
  const pageSheet =
    sheets.find(sheet => sheet.section_id === pageSection?.id) || {}
  const { id: sheetId } = pageSheet
  const allStyledElements = useSelector(visibleStyledElementsWorkspaceSelector)
  const tableLayoutIds = new Set(tableLayouts.map(l => l.id))
  const pageElements = allStyledElements.filter(
    element =>
      element.page_number === pageNumber &&
      !tableLayoutIds.has(element.layout_id)
  )
  const activePage = useSelector(activePageSelector) || {}
  const pageDefaultPosition = useSelector(
    pageDefaultPositionSelector(pageNumber)
  )
  const { scaleFactor = 1 } =
    useSelector(pdfPageMetaByPageNumberSelector(pageNumber)) || {}
  const { page_number: activePageNumber } = activePage

  const { over } = useDndContext()
  const isBeingDraggedOver = isTrueStringMatch(
    over?.id,
    `pdf-page-${pageNumber}`
  )

  const onClick = () => {
    dispatch(selectSheet({ sheet_id: sheetId }))
  }

  const { setNodeRef } = useDroppable({
    id: `pdf-page-${pageNumber}`,
    data: {
      accepts: [
        'dataInput',
        'pageElement',
        'resizeHandle',
        'tableDividerResize',
        'table',
        'tableMove',
      ],
      pageNumber,
      pageDefaultPosition,
      scaleFactor,
      page: thisPage,
      layout: pageLayout,
      existingScreenData: sheetId
        ? {
            section: pageSection,
            sheet: pageSheet,
          }
        : null,
      setGuidelineX,
      setGuidelineY,
      guidelineX,
      guidelineY,
    },
  })

  return (
    <div className="page--wrapper foundry--surface">
      {!!guidelineX && (
        <div
          className="guideline guideline-x"
          style={{
            '--guideline--position-x': `${
              Math.abs(guidelineX) / currentZoom
            }px`,
          }}
        />
      )}
      {!!guidelineY && (
        <div
          className="guideline guideline-y"
          style={{
            '--guideline--position-y': `${
              Math.abs(guidelineY) / currentZoom
            }px`,
          }}
        />
      )}
      <Page
        onClick={onClick}
        inputRef={setNodeRef}
        onLoadSuccess={onLoad}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        pageNumber={pageNumber}
        width={width}
        className={`pdf-page ${
          activePageNumber === pageNumber ? 'active' : ''
        } ${isBeingDraggedOver ? 'drag-target' : ''}`}
      >
        {tableLayouts.map(tableLayout => (
          <PdfTable
            key={tableLayout.id}
            tableLayout={tableLayout}
            tableLayoutIds={tableLayoutIds}
            thisPage={thisPage}
            sheets={sheets}
            allStyledElements={allStyledElements}
          />
        ))}
        {pageElements.map(element => (
          <PdfElement
            key={element.entry_guid}
            element={element}
            page={thisPage}
            layout={pageLayout}
          />
        ))}
      </Page>
    </div>
  )
}
